import React, { useState } from 'react'
import { Button, ConfigProvider, Form, message, Modal } from 'antd'
import { DeleteOutlined, EyeOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import MainTable from '../../components/Basic/MainTable'
import dayjs from 'dayjs'
import PopupConfirm from '../../components/Basic/PopupConfirm'
import { addDimension, deleteDimension, fetchDimension, modifyDimension } from '../../apis/indicator'
import DimensionForm from './DimensionForm'
import { useSelector } from 'react-redux'

function Dimension (props) {
  const { params, brandMapping, modalData, setModalData } = props
  const { t: ln_t } = useTranslation()
  const user = useSelector((state) => state.user)
  const { nowGroup } = user || {}
  const { fetchParams, handleFetchParams } = params
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [crossDataColumn, setCrossDataColumn] = useState([])

  const handleDelete = (id, data) => {
    Modal.confirm({
      title: `${ln_t('util.delete')} ${ln_t('indicator.dimensionSetting')}`,
      content: `${ln_t('message.confirm.delete')} ${data.name}?`,
      onOk: async () => {
        const result = await deleteDimension(id)
        if (result?.status === 204) {
          handleFetchParams('add', {})
          message.success(ln_t('message.success.delete'))
        } else if (result?.status === 403) {
          message.error(ln_t('message.error.dimensionIsUsed'))
        }
      }
    })
  }

  const handleChangeStatus = async (record, update) => {
    const result = await modifyDimension(record?.id, update)
    if (result?.status === 200) {
      message.success(ln_t('message.success.modify'))
      handleFetchParams('add', {})
    }
  }

  const handleView = data => {
    const { isCrossDataSource, dataSourceId, columnName } = data
    const formFields = isCrossDataSource !== 'yes'
      ? data
      : { ...data, tableName: dataSourceId, dataSourceId: 'crossData', columnName: columnName?.map(i => i?.split('^@^')?.slice(1)) }
    form.setFieldsValue(formFields)
    setModalData({ open: true, viewOnly: true, data: formFields })
  }

  const apiFunc = async () => fetchDimension(fetchParams)
  const columns = [
    { title: ln_t('indicator.name'), dataIndex: 'name', sorter: true },
    { title: ln_t('util.brand'), dataIndex: 'brandId', sorter: true, render: e => brandMapping[e] || e },
    {
      title: ln_t('util.status'),
      dataIndex: 'status',
      sorter: true,
      render: (e, record) => <PopupConfirm type="status" checked={e} onConfirm={() => handleChangeStatus(record, { status: e ? 0 : 1 })} />
    },
    { title: ln_t('util.createdAt'), dataIndex: 'createdAt', sorter: true, render: e => dayjs(e).format('YYYY-MM-DD') },
    { title: ln_t('util.updatedAt'), dataIndex: 'updatedAt', sorter: true, render: e => dayjs(e).format('YYYY-MM-DD') },
    {
      title: ln_t('util.action'),
      dataIndex: 'id',
      render: (id, data) => <div className="action-btn">
        <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(data)} />
        <Button size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(id, data)}/>
      </div>
    }
  ]

  const handleModalClose = () => {
    setModalData({ open: false })
    form.resetFields()
  }

  const findDataType = id => crossDataColumn?.find(i => i.value === id)?.dataSourceType
  const formatReqBody = value => {
    const isCrossDataSource = value?.dataSourceId === 'crossData'
    const def = { isCrossDataSource: isCrossDataSource ? 'yes' : 'no', groupId: nowGroup }
    if (!isCrossDataSource) return { ...value, columnName: value?.columnName?.join(','), ...def }
    return {
      ...value,
      ...def,
      dataSourceId: value?.tableName,
      tableName: '-',
      columnName: value?.columnName.map(i => [findDataType(i[0]), ...i]?.join('^@^'))?.join(',')
    }
  }
  const handleSave = async val => {
    // if (val) return console.log(formatReqBody(val))
    setLoading(true)
    const res = await addDimension(formatReqBody(val))
    if (res?.status === 201) {
      handleFetchParams('add', {})
      message.success(ln_t('message.success.add'))
      handleModalClose()
    }
    setLoading(false)
  }

  const passProps = { ...props, form, handleModalClose, loading, crossDataColumn, setCrossDataColumn }
  return <>
    <MainTable
      columns={columns}
      rowKey="id"
      apiFunc={apiFunc}
      params={params}
    />

    <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>
      <Modal title={ln_t('indicator.dimensionSetting')} open={modalData?.open || false} onCancel={handleModalClose} footer={null} destroyOnClose width={700}>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={handleSave} disabled={modalData?.viewOnly}>
          <DimensionForm {...passProps} />
        </Form>
      </Modal>
    </ConfigProvider>
  </>
}

export default Dimension
